import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Trans, withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Grid, Typography, TextField, Button } from '@material-ui/core';
import { InputMask } from '@react-input/mask';

// CONSTANTS
import { mobileAppDownloadLink } from '@/constants/common';

// UTILITY
import { analyticsIdentify, customAnalyticsEvent } from '@/utility/analytics';

// STORE
import { AppActions, NotifActions } from '@/store/actions';

// HOC
import withMediaQuery from '@/hoc/withMediaQuery';

// COMPONENTS
import Loader from '@/components/Loader';
import trimCharacters from '@/utility/common';

function getRawPhone(phone = '') {
  return `+${trimCharacters(phone)}`;
}
function validateProne(phone) {
  const rawPhone = getRawPhone(phone);

  return rawPhone.length === 12;
}

const MaskedInputComponent = ({ inputRef, ...rest }) => (
  <InputMask ref={inputRef} {...rest} mask="+1 ###-###-####" showMask replacement={{ '#': /\d/ }} />
);

MaskedInputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

// Shouldn't be a functional component because of @react-input/mask wrong behavior
class Hero extends PureComponent {
  state = {
    phone: '',
  };

  handleDownloadClick = link => {
    customAnalyticsEvent('Download App Clicked', { store: '', link });
  };

  handleSubmitPhone = e => {
    e.preventDefault();
    const { phone } = this.state;
    const {
      t,
      user,
      partner,
      partnerSettings,
      pushNotification,
      getDownloadAppLinkViaSMS,
    } = this.props;
    const downloadLink = partnerSettings.mobile_app_download_link || mobileAppDownloadLink;
    const rawPhone = getRawPhone(phone);
    const isValidPhone = validateProne(phone);

    if (!phone || !isValidPhone) return;

    const email = user.email || '';

    analyticsIdentify(rawPhone, { email, phone: rawPhone, applink: downloadLink });

    const onSuccess = () => {
      customAnalyticsEvent('Get a link to download the app', {
        phone: rawPhone,
        link: downloadLink,
        partner_name: partner.partner_name || '',
        partner_slug: partner.partner_slug || '',
      });

      pushNotification(t('notification.sendMobileNumberSuccess'));

      this.setState({ phone: '' });
    };

    getDownloadAppLinkViaSMS(
      { phone_number: rawPhone, partner: partner.partner_slug || null },
      onSuccess,
    );
  };

  handleChangePhone = e => {
    const value = e.target.value;

    if (value.startsWith('+1 1')) return;

    this.setState({ phone: value });
  };

  render() {
    const { phone } = this.state;
    const {
      t,
      downXs,
      sendDownloadLinkLoading,
      partnerSettings,
      textOverwrites,
      content,
      isIframe,
    } = this.props;

    const downloadLink = partnerSettings.mobile_app_download_link || mobileAppDownloadLink;

    const getDownloadLinkContent = () => {
      const isValidPhone = validateProne(phone);

      return downXs ? (
        <Button
          color="primary"
          type="button"
          component="a"
          href={downloadLink}
          target="_blank"
          fullWidth={isIframe}
          onClick={() => this.handleDownloadClick(downloadLink)}
        >
          {t('buttons.downloadApp')}
        </Button>
      ) : (
        <form onSubmit={this.handleSubmitPhone}>
          <Typography
            className="d-block m-b-10"
            variant="h6"
            component="label"
            htmlFor="phone-number"
          >
            {t('mobileApp.getLinkInputTitle')}
          </Typography>
          <div className="hero__number-input">
            <TextField
              id="phone-number"
              type="tel"
              InputProps={{
                inputComponent: MaskedInputComponent,
                endAdornment: (
                  <Button color="primary" type="submit" disabled={!!phone && !isValidPhone}>
                    {t('buttons.textMe')}
                  </Button>
                ),
              }}
              value={phone}
              onChange={this.handleChangePhone}
              placeholder="###-###-####"
              disabled={sendDownloadLinkLoading}
              required
            />
            <Loader active={sendDownloadLinkLoading} />
          </div>
        </form>
      );
    };

    function getImgContent() {
      const customImg = textOverwrites.app_landing_hero_img;
      const img = customImg || content.app_landing_hero_img;

      if (!img) return null;

      return <img className="hero__img" src={img} alt="phone" />;
    }

    const downloadLinkContent = getDownloadLinkContent();
    const imgContent = getImgContent();

    if (isIframe) {
      const appBenefits = [
        t('mobileApp.appBenefits.item1'),
        t('mobileApp.appBenefits.item2'),
        t('mobileApp.appBenefits.item3'),
        t('mobileApp.appBenefits.item4'),
        t('mobileApp.appBenefits.item5'),
      ];

      return (
        <div className="hero hero--iframe section">
          <div className="page-content">
            <Grid container spacing={4}>
              <Grid item xs={12} sm={8}>
                {downXs && downloadLinkContent}
                <Typography className="hero__title m-b-5" component="h1" variant="h2">
                  <Trans
                    i18nKey="mobileApp.title"
                    components={{
                      span: <span className="text-color--primary" />,
                    }}
                  />
                </Typography>
                <Typography className="hero__subtitle p1">{t('mobileApp.subtitle')}</Typography>
                {!!appBenefits?.length && (
                  <ul className="pros-cons-list pros-cons-list--square-icons">
                    {appBenefits.map((item, idx) => (
                      <li className="pcl-item" key={idx}>
                        <i className="icon-check" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {!downXs && downloadLinkContent}
              </Grid>
              <Grid item xs={12} sm={4}>
                {imgContent}
              </Grid>
            </Grid>
          </div>
        </div>
      );
    }

    return (
      <div className="hero section">
        <div className="page-content">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <Typography className="hero__title m-b-25" variant="h1">
                <Trans
                  i18nKey="mobileApp.title"
                  components={{
                    span: <span className="text-color--primary" />,
                  }}
                />
              </Typography>
              <Typography className="hero__subtitle p1">{t('mobileApp.subtitle')}</Typography>
              {downloadLinkContent}
            </Grid>
            <Grid item xs={12} sm={6}>
              {imgContent}
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }
}

Hero.propTypes = {
  t: PropTypes.func.isRequired,
  downXs: PropTypes.bool.isRequired,
  content: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  sendDownloadLinkLoading: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  partnerSettings: PropTypes.object.isRequired,
  textOverwrites: PropTypes.object.isRequired,
  getDownloadAppLinkViaSMS: PropTypes.func.isRequired,
  pushNotification: PropTypes.func.isRequired,
};

const mapStateToProps = ({ auth, app, partner: partnerStore, pages }) => ({
  content: pages.content,
  isIframe: app.isIframe,
  sendDownloadLinkLoading: app.loadings.sendDownloadLink,
  user: auth.user || {},
  partner: partnerStore.data,
  partnerSettings: partnerStore.data?.settings || {},
  textOverwrites: partnerStore.data?.text_overwrites || {},
});

const mapDispatchToProps = dispatch => {
  return {
    getDownloadAppLinkViaSMS: (data, onSuccess) =>
      dispatch(AppActions.getDownloadAppLinkViaSMS.action({ data, onSuccess })),
    pushNotification: message =>
      dispatch(
        NotifActions.pushNotification.action({
          message,
          options: {
            variant: 'success',
          },
        }),
      ),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withMediaQuery({ downXs: theme => theme.breakpoints.down('xs') }),
)(Hero);
